import { useEffect } from "react";
import "@/index.css";
import {
  BrowserRouter, Routes, Route, Outlet, useLocation,
} from "react-router-dom";
import { ReactLenis, useLenis } from "lenis/react";
import { HelmetProvider } from "react-helmet-async";
import { motion } from "framer-motion";

import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import ProtectedRoute from "@/components/ProtectedRoute";

import HomePage from "@/pages/Home";
import AboutPage from "@/pages/About";
import ServicesPage from "@/pages/Services";
import IndustriesPage from "@/pages/Industries";
import PortfolioPage from "@/pages/Portfolio";
import CaseStudiesPage from "@/pages/CaseStudies";
import ProcessPage from "@/pages/Process";
import PricingPage from "@/pages/Pricing";
import BlogPage from "@/pages/Blog";
import BlogPostPage from "@/pages/BlogPost";
import FAQPage from "@/pages/FAQ";
import ContactPage from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const prefersReduced =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function ScrollToTop() {
  const { pathname } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, lenis]);
  return null;
}

function MarketingLayout() {
  const { pathname } = useLocation();
  return (
    <>
      <Navbar />
      <motion.main
        key={pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <Outlet />
      </motion.main>
      <Footer />
      <FloatingActions />
      <ExitIntentPopup />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ReactLenis
          root
          options={{ lerp: 0.09, smoothWheel: !prefersReduced, duration: 1.1 }}
        >
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route element={<MarketingLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/industries" element={<IndustriesPage />} />
                <Route path="/portfolio" element={<PortfolioPage />} />
                <Route path="/case-studies" element={<CaseStudiesPage />} />
                <Route path="/process" element={<ProcessPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/contact" element={<ContactPage />} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </ReactLenis>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
